import { useRef, useCallback } from "react";
import { CurrentUser, Post } from "../../../types/types";
import PostCard from "../PostCard";
import PostSkeleton from "../PostSkeleton";

interface PostListProps {
  posts: Post[];
  currentUser: CurrentUser;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  onFetchNextPage: () => void;
  onLike: (postId: number) => void;
  onDelete: (postId: number) => void;
  deletingId: number | null;
}

const PostList: React.FC<PostListProps> = ({
  posts,
  currentUser,
  isFetchingNextPage,
  hasNextPage,
  onFetchNextPage,
  onLike,
  onDelete,
  deletingId,
}) => {
  const observer = useRef<IntersectionObserver | null>(null);

  const lastPostRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage || !node) return;
      observer.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          onFetchNextPage();
        }
      });
      observer.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, onFetchNextPage]
  );

  return (
    <div className="space-y-6 pb-32">
      {posts.map((post, i) => {
        const isLast = i === posts.length - 1;
        return (
          <div key={post.id} ref={isLast ? lastPostRef : null}>
            <PostCard
              post={post}
              currentUser={currentUser}
              isDeleting={deletingId === post.id}
              onLike={onLike}
              onDelete={onDelete}
            />
          </div>
        );
      })}

      {isFetchingNextPage && <PostSkeleton count={2} />}
      {!hasNextPage && posts.length > 8 && (
        <div className="text-center py-12 text-gray-500 font-medium">
          You're all caught up!
        </div>
      )}
    </div>
  );
};

export default PostList;
